
import * as React from "react";
import { format, addSeconds } from "date-fns";
import { GameContent } from "./ticker/GameContent";
import { LogoSection } from "./ticker/LogoSection";
import { AlertSection } from "./ticker/AlertSection";
import { BottomTicker } from "./ticker/BottomTicker";
import { TSPEvent } from "@/types/tsp";
import { KitLogo } from "@/hooks/useKitLogo";
import "./ticker/ticker-styles.css";

interface TickerPreviewProps {
  messages: string[];
  speed: number;
  backgroundColor: string;
  textColor: string;
  alertMessage?: string;
  alertImages?: {
    storage_path: string;
    duration: number;
    is_active: boolean;
  }[];
  logo?: KitLogo | null;
  upcomingGames?: TSPEvent[];
  selectedMarket: string;
  weatherTemp?: number | null;
  weatherCondition?: string | null;
}

const TickerPreviewComponent: React.FC<TickerPreviewProps> = ({ 
  messages,
  speed,
  backgroundColor,
  textColor,
  alertMessage,
  alertImages = [],
  logo,
  upcomingGames = [],
  selectedMarket,
  weatherTemp,
  weatherCondition,
}) => {
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [currentGameIndex, setCurrentGameIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [prevGameIndex, setPrevGameIndex] = React.useState(0);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [previousImageIndex, setPreviousImageIndex] = React.useState(0);
  const [showUpcomingInTicker] = React.useState(() => {
    const stored = localStorage.getItem('show-upcoming-in-ticker');
    return stored ? JSON.parse(stored) : false;
  });

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const adjustedTime = addSeconds(currentTime, 40);
  
  const activeImages = React.useMemo(() => 
    alertImages.filter(img => img.is_active)
  , [alertImages]);

  const displayImage = alertMessage ? null : activeImages[currentImageIndex];
  const showAlertMessage = alertMessage ? true : false;
  const TICKER_ASPECT_RATIO = 350 / 80;

  const formatWeather = React.useCallback(() => {
    if (weatherTemp === null || weatherTemp === undefined) return '';
    return ` ${weatherTemp}°F${weatherCondition ? ` ${weatherCondition}` : ''}`;
  }, [weatherTemp, weatherCondition]);

  React.useEffect(() => {
    if (alertMessage) {
      setPreviousImageIndex(currentImageIndex);
      return;
    } else {
      setCurrentImageIndex(previousImageIndex);
    }

    if (activeImages.length <= 1) return;
    
    const timer = setTimeout(() => {
      setCurrentImageIndex(prev => (prev + 1) % activeImages.length);
    }, displayImage?.duration || 5000);
    
    return () => clearTimeout(timer);
  }, [currentImageIndex, activeImages, displayImage?.duration, alertMessage, previousImageIndex]);

  React.useEffect(() => {
    if (upcomingGames.length <= 1) return;

    const timer = setInterval(() => {
      setPrevGameIndex(currentGameIndex);
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentGameIndex(prev => (prev + 1) % upcomingGames.length);
        setIsTransitioning(false);
      }, 300);
    }, 13000);

    return () => clearInterval(timer);
  }, [upcomingGames.length, currentGameIndex]);

  const tickerMessages = React.useMemo(() => {
    let combinedMessages = [...messages];
    
    if (showUpcomingInTicker) {
      const upcomingGameMessages = upcomingGames.map(game => 
        `${game.meta.event_away_team_short_name} vs ${game.meta.event_home_team_short_name} - ${format(new Date(`${game.meta.event_date} ${game.meta.event_time}`), 'MMM d, h:mm a')}`
      );
      combinedMessages = [...combinedMessages, ...upcomingGameMessages];
    }
    
    return combinedMessages;
  }, [messages, upcomingGames, showUpcomingInTicker]);

  const currentGameDisplay = React.useMemo(() => upcomingGames[currentGameIndex], [upcomingGames, currentGameIndex]);
  const prevGameDisplay = React.useMemo(() => upcomingGames[prevGameIndex], [upcomingGames, prevGameIndex]);

  return (
    <div className="relative w-[1920px]">
      <div className="h-[69px] flex">
        <LogoSection logo={logo} />

        <div 
          className="w-[1220px] h-full overflow-hidden shrink-0 flex flex-col"
          style={{ backgroundColor }}
        >
          <div className="h-[31px] flex pt-[2px]">
            <div className="game-container flex-1 pl-8">
              {prevGameDisplay && isTransitioning && (
                <GameContent 
                  game={prevGameDisplay} 
                  className="exit"
                  textColor={textColor}
                />
              )}
              {currentGameDisplay && (
                <GameContent 
                  game={currentGameDisplay}
                  className={isTransitioning ? 'enter' : 'active'}
                  textColor={textColor}
                />
              )}
            </div>
            <div 
              className="flex items-center px-8 border-l h-[22px] my-auto shrink-0"
              style={{ borderColor: `${textColor}80`, borderLeftWidth: '2px' }}
            >
              <div className="text-[16px] font-[Helvetica] whitespace-nowrap" style={{ color: textColor }}>
                {selectedMarket}{formatWeather()} {format(adjustedTime, 'h:mm:ss a')} CST
              </div>
            </div>
          </div>

          <BottomTicker messages={tickerMessages} textColor={textColor} speed={speed} />
        </div>

        <AlertSection 
          displayImage={displayImage}
          showAlertMessage={showAlertMessage}
          alertMessage={alertMessage}
          aspectRatio={TICKER_ASPECT_RATIO}
        />
      </div>
    </div>
  );
};

export const TickerPreview = React.memo(TickerPreviewComponent);
TickerPreview.displayName = 'TickerPreview';
